import { CommonModule } from "@angular/common";
import { Component, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MastersService } from "@app/core/services/masters.service";
import {
  isMyAisChannel,
  isNetworkTypeChannel,
} from "@app/shared/constants/channel.constants";
import { isFlatEnrichmentModel } from "@app/shared/mappers/enrichment-detail.adapter";
import { normalizeOfferClassification } from "@app/feature/management/enrichment/mappers/poc.util";
import { IEnrichmentModel } from "@app/shared/models/enrichment.model";
import { LanguageModeService } from "@app/shared";
import { logger } from "@core/utils/logger";
import { OmniarkUiModule, SelectOption } from "@corp-ais/omniark-ui";
import { PACKAGE_SETTINGS_TAB } from "../../enrichment-edit-package/tab-algebra.util";
import { EnrichmentTabBase } from "../_shared/enrichment-tab.base";

export const PACKAGE_TYPE_VALUES = ["Sim2Fly", "Gomo"] as const;

/** Single source of truth for the productEffectiveOption codes. The `topic`
 *  doubles as the select label. Only the option code is persisted now (BE
 *  stores `productEffectiveOption` as a plain string array); `description` is
 *  kept here as reference copy for the UI. */
interface IProductEffectiveOptionMeta {
  option: string;
  topic: string;
  description: string;
}

export const PRODUCT_EFFECTIVE_OPTION_META: readonly IProductEffectiveOptionMeta[] =
  [
    {
      option: "I",
      topic: "Immediately",
      description:
        "Your package will be effective immediately after SMS confirmation.",
    },
    {
      option: "D",
      topic: "Next day",
      description:
        "Your package will be effective the next day after SMS confirmation.",
    },
    {
      option: "B",
      topic: "Next billing cycle",
      description:
        "Your package will be effective at the start of your next billing cycle.",
    },
    {
      option: "F",
      topic: "Specify start date (up to 30 days in advance)",
      description:
        "Your package will be effective on the specified start date.",
    },
  ];

/** FE fallback used when `GET /api/v1/master/segment` returns an empty
 *  list — the BE master is not seeded yet, so the editor would otherwise
 *  show "No segments available" and block the user from picking a tier.
 *  Remove this fallback once BE seeds the master. */
export const SEGMENT_FALLBACK: readonly { id: string; label: string }[] = [
  { id: "classic", label: "Classic" },
  { id: "emerald", label: "Emerald" },
  { id: "gold", label: "Gold" },
  { id: "platinum", label: "Platinum" },
];

interface ProductAttributesFormValue {
  networkType: string[];
  segment: string[];
  productEffectiveOption: string[];
  offerClassificationName: string;
  packageType: string;
  /** Absent from the form value while the control is disabled (catalog-owned). */
}

@Component({
  selector: "app-enrichment-tab-product-attributes",
  imports: [CommonModule, ReactiveFormsModule, OmniarkUiModule],
  templateUrl: "./enrichment-tab-product-attributes.html",
  styleUrl: "./enrichment-tab-product-attributes.scss",
})
export class EnrichmentTabProductAttributes extends EnrichmentTabBase {
  /** Every commit through the base class's `commit()` stamps this tab as
   *  dirty in `SectionDirtyStore`, when one is provided. */
  protected override readonly sectionTabId = PACKAGE_SETTINGS_TAB;

  private readonly fb = inject(FormBuilder);
  private readonly masters = inject(MastersService);
  private readonly languageMode = inject(LanguageModeService);

  /** Effective-option choices from the master API (`/master/effective`),
   *  localized by language. Empty until {@link loadEffectiveOptions} resolves —
   *  the picker then falls back to the static `PRODUCT_EFFECTIVE_OPTION_META`. */
  private readonly effectiveMaster = signal<SelectOption[]>([]);

  /** The channel the user is editing under (from `EnrichmentDataService`).
   *  Only the Segment section renders for every channel; Network Type also
   *  renders on AI Chatbot; the rest of this tab is myAIS-only. Empty/unknown
   *  is treated as myAIS so the default behavior (show everything) is
   *  preserved. */
  private readonly channel = toSignal(this.data.channel$, {
    initialValue: "" as const,
  });

  /** Drives the `@if` gate on the myAIS-only sections in the template. */
  readonly isMyAis = computed(() => isMyAisChannel(this.channel()));
  readonly showNetworkType = computed(() =>
    isNetworkTypeChannel(this.channel()),
  );

  override readonly form: FormGroup = this.fb.group({
    networkType: [[] as string[]],
    segment: [[] as string[]],
    productEffectiveOption: [[] as string[]],
    offerClassificationName: [""],
    packageType: [""],
  });

  networkTypeOptions: SelectOption[] = [];
  segmentOptions: SelectOption[] = [];

  readonly packageTypeOptions: SelectOption[] = PACKAGE_TYPE_VALUES.map(
    (v) => ({
      value: v,
      label: v,
    }),
  );

  /** Per-dropdown load state — drives the placeholder ("Loading…" vs
   *  "Please select" vs "No options"). Separate from the bound value so a
   *  failed load doesn't leave the chip stuck on "Loading…" forever. */
  protected networkTypeLoading = false;
  protected networkTypeLoaded = false;
  protected segmentLoading = false;
  protected segmentLoaded = false;

  /** Captured from the flat `__detail.productType` in `hydrate()`. Feeds the
   *  network-type master filter AND {@link isPrepaid} (drives the effective-
   *  option filtering). A signal so both react when the model arrives. */
  private readonly productType = signal<string | undefined>(undefined);

  /** True for a prepaid package (`productType` normalised to "prepaid",
   *  case- + separator-insensitive) — matches the Pricing tab's detection. */
  readonly isPrepaid = computed<boolean>(
    () =>
      (this.productType() ?? "").toLowerCase().replaceAll(/[^a-z]/g, "") ===
      "prepaid",
  );

  /** Placeholder reflects the active load state for each dropdown — the
   *  template binds this directly so users see meaningful feedback. */
  get networkTypePlaceholder(): string {
    if (this.networkTypeLoading) return "Loading…";
    if (this.networkTypeLoaded && this.networkTypeOptions.length === 0)
      return "No network types available";
    return "Please select";
  }

  get segmentPlaceholder(): string {
    if (this.segmentLoading) return "Loading…";
    if (this.segmentLoaded && this.segmentOptions.length === 0)
      return "No segments available";
    return "Please select";
  }

  /** Effective-option choices — sourced from the `/master/effective` API
   *  (localized), falling back to the static `PRODUCT_EFFECTIVE_OPTION_META`
   *  when the master is empty/unavailable. "Next billing cycle" (`B`) is
   *  dropped for prepaid packages — prepaid has no recurring billing cycle
   *  (the BE doesn't split prepaid/postpaid, so it's filtered here; mirrors the
   *  Billing cycle section being hidden for prepaid on the Pricing tab). */
  readonly productEffectiveOptions = computed<SelectOption[]>(() => {
    const fromApi = this.effectiveMaster();
    const base = fromApi.length
      ? fromApi
      : PRODUCT_EFFECTIVE_OPTION_META.map((m) => ({
          value: m.option,
          label: m.topic,
        }));
    return base.filter((o) => !(this.isPrepaid() && o.value === "B"));
  });

  /** Fetch the effective-option master for the active view language and cache
   *  it into {@link effectiveMaster}. `languageId` mirrors the ENR_EFFECTIVE
   *  seed (1 = TH, 2 = EN); anything else falls back to EN. On empty/error the
   *  signal stays empty so the static list is used. */
  private loadEffectiveOptions(): void {
    const locale = this.languageMode.activeLocale();
    const languageId = locale.toLowerCase().startsWith("th") ? 1 : 2;
    this.masters
      .getMasterEffective(languageId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const rows = res?.data ?? [];
          this.effectiveMaster.set(
            rows
              .filter((r) => (r.useFlag ?? "Y") === "Y" && !!r.option)
              .map((r) => ({ value: r.option, label: r.topic })),
          );
        },
        error: (err) => {
          logger.error("Failed to load effective-option master", err);
          this.effectiveMaster.set([]);
        },
      });
  }

  protected override onAfterInit(): void {
    // Segment + effective-option masters have no model dependency — load them
    // up front. Network types are loaded from hydrate() instead, once
    // productType is known.
    this.loadSegmentOptions();
    this.loadEffectiveOptions();
  }

  protected override hydrate(model: IEnrichmentModel): void {
    // Product-Attributes reads the flat root fields straight off the detail.
    // The edit shell always feeds flat models (via detailToModel), so this
    // resolves on the first hydrate.
    const detail = isFlatEnrichmentModel(model) ? model.__detail : undefined;
    // Capture productType so the network-type master can be filtered by it,
    // and so isPrepaid resolves before we filter the effective-option value.
    this.productType.set(detail?.productType);
    // Guarded — loads once, on the first model that arrives.
    this.loadNetworkTypeOptions();

    const networkType = detail?.networkType ?? [];
    // Drop "Next billing cycle" (B) for prepaid — the option isn't offered, so
    // a stale saved value would otherwise show as a dangling chip / round-trip.
    const productEffectiveOption = (
      detail?.productEffectiveOption ?? []
    ).filter((o) => !(this.isPrepaid() && o === "B"));
    const segment = detail?.segment ?? [];
    // Fold the source/historical spelling (Main, On-Top, Ontop Extra, the
    // bo_lov_offerclassification_* LOV codes, …) onto a canonical code so the
    // select matches one of its three options. A value that resembles none of
    // them keeps its raw form and is augmented in as its own option below, so
    // unexpected future data is still shown rather than silently dropped.
    const rawOfferClassification = detail?.offerClassificationName ?? "";
    const offerClassificationName =
      normalizeOfferClassification(rawOfferClassification) ||
      rawOfferClassification.trim();
    // packageType is a single-select in the UI; the root field is an array.
    const packageType = detail?.packageType?.[0] ?? "";

    this.form.patchValue({
      networkType,
      segment,
      productEffectiveOption,
      offerClassificationName,
      packageType: (PACKAGE_TYPE_VALUES as readonly string[]).includes(
        packageType,
      )
        ? packageType
        : "",
    });

    this.augmentSegmentOptions(segment);
    this.augmentNetworkTypeOptions(networkType);
  }

  private loadSegmentOptions(): void {
    if (this.segmentLoaded || this.segmentLoading) return;
    this.segmentLoading = true;
    this.masters
      .getMasterSegment()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.segmentLoading = false;
          this.segmentLoaded = true;
          const data = Array.isArray(res?.data) ? res.data : [];
          const fromBE = data
            .map((item) => normalizeSegmentMaster(item))
            .filter((opt): opt is SelectOption => !!opt);
          this.segmentOptions = fromBE.length
            ? fromBE
            : this.buildFallbackSegments();
          this.augmentSegmentOptions(
            this.form.get("segment")?.value as string[] | null,
          );
          this.resyncControl("segment");
        },
        error: (err) => {
          this.segmentLoading = false;
          this.segmentLoaded = true;
          logger.error("Failed to load segment master", err);
          // BE down → still let the user pick a tier from the FE fallback.
          this.segmentOptions = this.buildFallbackSegments();
          this.augmentSegmentOptions(
            this.form.get("segment")?.value as string[] | null,
          );
          this.resyncControl("segment");
        },
      });
  }

  /** Materialize the fallback constant into the `SelectOption[]` shape
   *  the rest of the tab consumes. Kept here so the constant stays a
   *  plain data list (easy to read / delete when BE seeds the master). */
  private buildFallbackSegments(): SelectOption[] {
    return SEGMENT_FALLBACK.map((s) => ({ value: s.id, label: s.label }));
  }

  /** Load network types from `GET /api/v1/master/nType`, filtered by the
   *  package's `productType` (sourced from the flat `__detail`, the reliable
   *  field — an earlier attempt to read it from a POC entry was unreliable
   *  because most packages don't carry that characteristic). When productType
   *  is absent the full list loads. Either way, the saved-value augmentation
   *  in `hydrate()` keeps any value the master endpoint omits visible. */
  private loadNetworkTypeOptions(): void {
    if (this.networkTypeLoaded || this.networkTypeLoading) return;
    this.networkTypeLoading = true;
    this.masters
      .getMasterNtype(this.productType())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.networkTypeLoading = false;
          this.networkTypeLoaded = true;
          const data = res?.data ?? [];
          this.networkTypeOptions = data
            .filter((item) => item?.useFlag === "Y" && !!item?.ntypeName)
            .map((item) => ({
              value: item.ntypeName,
              label: item.mobileType
                ? `${item.mobileType} (${item.ntypeName})`
                : item.ntypeName,
            }));
          this.augmentNetworkTypeOptions(
            this.form.get("networkType")?.value as string[] | null,
          );
          this.resyncControl("networkType");
        },
        error: (err) => {
          this.networkTypeLoading = false;
          this.networkTypeLoaded = true;
          this.networkTypeOptions = [];
          logger.error("Failed to load network type master", err);
        },
      });
  }

  /** Ensure every segment id present in the model has a matching option so
   *  the chip can render even if the master endpoint omits it. The master
   *  supplies the human label for known ids; ids it doesn't cover fall back
   *  to showing the id itself. */
  private augmentSegmentOptions(values: string[] | null | undefined): void {
    if (!values?.length) return;
    const additions: SelectOption[] = [];
    for (const id of values) {
      if (!id) continue;
      if (this.segmentOptions.some((o) => o.value === id)) continue;
      if (additions.some((o) => o.value === id)) continue;
      additions.push({ value: id, label: id });
    }
    if (additions.length) {
      this.segmentOptions = [...this.segmentOptions, ...additions];
    }
  }

  /** Ensure every saved networkType value has a matching option (e.g. the
   *  master flipped useFlag to N after the package was saved). */
  private augmentNetworkTypeOptions(values: string[] | null | undefined): void {
    if (!values?.length) return;
    const additions: SelectOption[] = [];
    for (const value of values) {
      if (!value) continue;
      if (this.networkTypeOptions.some((o) => o.value === value)) continue;
      if (additions.some((o) => o.value === value)) continue;
      additions.push({ value, label: value });
    }
    if (additions.length) {
      this.networkTypeOptions = [...this.networkTypeOptions, ...additions];
    }
  }

  /** Re-emit the current form value so enr-select rebinds against freshly
   *  loaded options. emitEvent:false keeps this from triggering a commit. */
  private resyncControl(controlName: "segment" | "networkType"): void {
    const ctrl = this.form.get(controlName);
    if (!ctrl) return;
    const current = ctrl.value;
    const next = Array.isArray(current) ? [...current] : current;
    ctrl.setValue(next, { emitEvent: false });
  }

  protected override serialize(
    model: IEnrichmentModel,
    value: ProductAttributesFormValue,
  ): IEnrichmentModel {
    if (!model.productOffering) return model;

    // Write the flat root fields straight onto `__detail` (the shared reference
    // the adapter reads on save), mirroring the Term tab's agreements pattern.
    // `productEffectiveOption` holds the bare option codes; `packageType` is a
    // single-select in the UI but a string array on the wire. An empty array
    // (incl. cleared offerClassificationName "") replaces the stored value.
    if (isFlatEnrichmentModel(model)) {
      model.__detail.networkType = value.networkType ?? [];
      model.__detail.segment = value.segment ?? [];
      model.__detail.productEffectiveOption =
        value.productEffectiveOption ?? [];
      // The form holds a canonical code (hydrate normalized it) or, for a value
      // that matched no known token, the raw catalog string — write it verbatim
      // so picking an option also migrates a legacy spelling on save.
      model.__detail.offerClassificationName =
        value.offerClassificationName ?? "";
      model.__detail.packageType =
        value.packageType &&
        (PACKAGE_TYPE_VALUES as readonly string[]).includes(value.packageType)
          ? [value.packageType]
          : [];
    }

    return model;
  }
}

/** Master-segment entries can come back as { id, name } objects or as bare
 *  strings depending on backend version. We always use id as the option value
 *  (stable across renames) and fall back to name when id is missing. */
export function normalizeSegmentMaster(raw: unknown): SelectOption | null {
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    return trimmed ? { value: trimmed, label: trimmed } : null;
  }
  if (!raw || typeof raw !== "object") return null;
  const data = raw as {
    id?: unknown;
    name?: unknown;
    segmentName?: unknown;
    label?: unknown;
    value?: unknown;
  };
  const idCandidate = pickString(data.id, data.value);
  const nameCandidate = pickString(
    data.name,
    data.segmentName,
    data.label,
    data.id,
    data.value,
  );
  const value = idCandidate || nameCandidate;
  const label = nameCandidate || idCandidate;
  if (!value || !label) return null;
  return { value, label };
}

function pickString(...candidates: unknown[]): string {
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
    if (typeof c === "number") return String(c);
  }
  return "";
}
